/**
 * Translate Convex backend error codes into user-facing copy.
 *
 * The Convex mutations in `frontend/convex/auth.ts` throw `new Error(CODE)`
 * where CODE is one of the strings in `convex/lib/types.ts`. The Convex
 * client surfaces these as plain `Error` instances on the frontend; we
 * detect by matching `error.message` (or its `data` payload) against the
 * known set.
 */

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  ALREADY_LOGGED_IN:
    'You are already signed in on another device. Sign out there first, or close that window.',
  INVALID_CREDENTIALS:
    'That email and password combination did not match. Try again.',
  EMAIL_TAKEN:
    'An account already exists for that email. Try signing in instead.',
  WEAK_PASSWORD:
    'Use at least eight characters, with at least one letter and one digit.',
  INVALID_EMAIL:
    'That email address looks malformed. Check the spelling.',
  SESSION_SUPERSEDED:
    'Your session was taken over by another login. Please sign in again.',
  NOT_AUTHENTICATED:
    'You are not signed in.',
  NOT_AUTHORIZED:
    'You do not have access to this resource.',
  NOT_FOUND:
    'We could not find what you were looking for.',
  LIMIT_EXCEEDED:
    'You have reached the limit for this operation.',
  VALIDATION_FAILED:
    'Some of the information you entered is invalid.',
  INVALID_SIGNUP_SECRET:
    'The invitation key is incorrect. Check with the person who sent you access.',
  SIGNUP_DISABLED:
    'New sign-ups are closed for now. Contact the study team if you need an account.',
  ADMIN_NOT_AUTHENTICATED: 'Your experimenter session has expired. Sign in again.',
  ADMIN_INVALID_CREDENTIALS: 'Wrong experimenter username or password.',
  ADMIN_RATE_LIMITED: 'Too many failed sign-in attempts. Wait fifteen minutes and try again.',
  ADMIN_NOT_CONFIGURED:
    'The experimenter panel is not configured: set ADMIN_USERNAME and ADMIN_PASSWORD on the Convex deployment.',
};

const KNOWN_CODES = new Set(Object.keys(AUTH_ERROR_MESSAGES));

/**
 * Pull the AURA error code out of an unknown error from Convex.
 * Returns null if the error doesn't look like one of ours.
 */
export function extractErrorCode(err: unknown): string | null {
  if (err instanceof Error) {
    // Convex 1.x surfaces the thrown message inline; some versions wrap
    // it. Search the stringified message for any known code.
    const msg = err.message;
    for (const code of KNOWN_CODES) {
      if (msg.includes(code)) return code;
    }
  }
  return null;
}

/**
 * One-shot helper: take an unknown error, return a friendly message.
 * Falls back to a generic error string if no code matches.
 */
export function friendlyAuthError(err: unknown): string {
  const code = extractErrorCode(err);
  if (code && AUTH_ERROR_MESSAGES[code]) return AUTH_ERROR_MESSAGES[code];
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Please try again.';
}
